import type { WhatsAppWorkerHealth } from "../../domain/contracts/queue";
import {
  Badge,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  cn,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  Loader2Icon,
  QrCodeIcon,
} from "lucide-react";
import type { ReactNode } from "react";

type StatusConfig = {
  label: string;
  badgeClassName: string;
  icon: ReactNode;
  details: ReactNode;
};

function getStatusConfig(
  health: WhatsAppWorkerHealth,
  embeddedOnSettingsPage: boolean,
): StatusConfig {
  if (health.status === "connected") {
    return {
      label: "WhatsApp ພ້ອມໃຊ້ງານ",
      badgeClassName: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700",
      icon: <CheckCircle2Icon className="size-3.5 shrink-0" />,
      details: (
        <>
          <p className="font-medium">Worker ເຊື່ອມຕໍ່ແລ້ວ</p>
          {health.messagesToday != null ? (
            <p className="text-muted-foreground">
              ສົ່ງແລ້ວມື້ນີ້ {health.messagesToday} ຂໍ້ຄວາມ
            </p>
          ) : null}
        </>
      ),
    };
  }

  if (health.needsQr || health.status === "needs_qr") {
    return {
      label: "ຕ້ອງ scan QR",
      badgeClassName: "border-amber-500/40 bg-amber-500/10 text-amber-700",
      icon: <QrCodeIcon className="size-3.5 shrink-0" />,
      details: (
        <>
          <p className="font-medium">ຕ້ອງ scan QR ເພື່ອເຊື່ອມ WhatsApp</p>
          <p className="text-muted-foreground">
            {embeddedOnSettingsPage
              ? "ສະແກນ QR ດ້ານລຸ່ມດ້ວຍເບີ WhatsApp Studio"
              : "WhatsApp Worker ຕ້ອງການເຊື່ອມຕໍ່ໃໝ່"}
          </p>
          {!embeddedOnSettingsPage ? (
            <Link
              to="/app/settings/package-notifications"
              className="font-medium text-primary underline"
            >
              ເປີດຫນ້າຕັ້ງຄ່າ
            </Link>
          ) : null}
        </>
      ),
    };
  }

  if (health.status === "not_configured") {
    return {
      label: "ຍັງບໍ່ຕັ້ງຄ່າ Worker",
      badgeClassName: "border-amber-500/40 bg-amber-500/10 text-amber-700",
      icon: <AlertTriangleIcon className="size-3.5 shrink-0" />,
      details: (
        <>
          <p className="font-medium">ຍັງບໍ່ໄດ້ຕັ້ງຄ່າ Worker</p>
          <p className="text-muted-foreground">
            {embeddedOnSettingsPage ? (
              <>
                ກະລຸນາໃສ່ Worker URL ໃນຟອມ (ຕົວຢ່າງ{" "}
                <code className="rounded bg-muted px-1">
                  http://127.0.0.1:3100
                </code>
                ) ແລ້ວກົດບັນທຶກ
              </>
            ) : (
              <>
                ໄປ{" "}
                <Link
                  to="/app/settings/package-notifications"
                  className="font-medium text-primary underline"
                >
                  ຫນ້າຕັ້ງຄ່າ
                </Link>{" "}
                ແລ້ວໃສ່ Worker URL
              </>
            )}
          </p>
        </>
      ),
    };
  }

  if (health.status === "initializing") {
    return {
      label: "Worker ກຳລັງເລີ່ມ...",
      badgeClassName: "border-amber-500/40 bg-amber-500/10 text-amber-700",
      icon: <Loader2Icon className="size-3.5 shrink-0 animate-spin" />,
      details: (
        <>
          <p className="font-medium">Worker ກຳລັງເລີ່ມ...</p>
          <p className="text-muted-foreground">
            ລໍຖ້າສักຄູ່ແລ້ວ refresh — ກຳລັງເຊື່ອມ WhatsApp
          </p>
        </>
      ),
    };
  }

  return {
    label: "Worker ບໍ່ຕອບ",
    badgeClassName: "border-destructive/40 bg-destructive/10 text-destructive",
    icon: <AlertTriangleIcon className="size-3.5 shrink-0" />,
    details: (
      <>
        <p className="font-medium">Worker ບໍ່ຕອບ</p>
        <p className="text-muted-foreground">
          ກວດວ່າ Worker ກຳລັງຮັນຢູ່ ແລະ URL ຖືກຕ້ອງ — ຫຼືໃຊ້ປຸ່ມ wa.me manual
          ແທນ
        </p>
      </>
    ),
  };
}

export function WorkerStatusBanner({
  health,
  embeddedOnSettingsPage = false,
  className,
}: {
  health?: WhatsAppWorkerHealth;
  embeddedOnSettingsPage?: boolean;
  className?: string;
}) {
  if (!health) return null;

  const config = getStatusConfig(health, embeddedOnSettingsPage);

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex cursor-default rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            className,
          )}
        >
          <Badge
            variant="outline"
            className={cn("gap-1.5 font-normal", config.badgeClassName)}
          >
            {config.icon}
            {config.label}
          </Badge>
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-80 space-y-2 p-3 text-sm">
        {config.details}
      </HoverCardContent>
    </HoverCard>
  );
}

export function maskPhoneNumber(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 6) return phone;
  return `${digits.slice(0, 3)}***${digits.slice(-3)}`;
}

export const TRIGGER_LABELS: Record<string, string> = {
  low_sessions: "ເຫຼືອຄັ້ງໜ້ອຍ",
  low_hours: "ເຫຼືອຊົ່ວໂມງໜ້ອຍ",
  expiring_soon: "ໃກ້ໝົດອາຍຸ",
  combined: "ລວມ (ເຫຼືອ + ໝົດອາຍຸ)",
};
